import type {CreateExtArgs, EnvironmentContext} from '@wix/document-manager-core'
import type {SiteContext, SiteMetadata} from './types'
import type {PageExtensionAPI} from '../../page'
import type {ThemeExtAPI} from '../../theme/theme'
import {aiGatewayUrl, proactiveAiBuildSiteContextPromptId, promptHubAppId} from '../constants'
import {ReportableError} from '@wix/document-manager-utils'
import {VIEW_MODES} from '../../../constants/constants'
import {getPageOutlineAndTargetsForBuilder, type BuilderOutline} from '../aiBuilderGeneration'

const proactiveAiBuildContextErrorType = 'ProactiveAiBuildContextError'
const proactiveAiBuildContextErrorMessage = 'Failed to build site context from AI'

const getSiteMetadata = (createExtArgs: CreateExtArgs): SiteMetadata => {
    const {dal, pointers, extensionAPI} = createExtArgs
    const {theme} = extensionAPI as ThemeExtAPI

    const siteDisplayNamePointer = pointers.rendererModel.getSiteDisplayName()
    const siteName = dal.get(siteDisplayNamePointer) ?? ''

    const siteTitlePointer = pointers.rendererModel.getSiteTitleSEO()
    const siteTitleSEO = dal.get(siteTitlePointer) ?? ''

    const brandColors = Object.values(theme.getColorsConsideringGlobalVariables())
    const brandFonts = Object.values(theme.getFontsConsideringGlobalVariables())

    return {
        siteName: siteName || siteTitleSEO || '',
        brandColors,
        brandFonts
    }
}

const getMainPageOutline = (createExtArgs: CreateExtArgs): BuilderOutline => {
    const {extensionAPI, pointers} = createExtArgs
    const {page: pageExt} = extensionAPI as PageExtensionAPI

    const mainPageId = pageExt.getMainPageId()
    const pagePointer = pointers.structure.getPage(mainPageId, VIEW_MODES.DESKTOP)

    return getPageOutlineAndTargetsForBuilder(createExtArgs, pagePointer).outline
}

const parseAISiteContextResponse = (responseText: string): SiteContext => {
    const jsonMatch = responseText.match(/\{[\s\S]*\}/)
    if (!jsonMatch) {
        throw new Error('No valid JSON found in AI response')
    }

    const parsed = JSON.parse(jsonMatch[0])

    return {
        brand: {
            voice: parsed.brand?.voice ?? 'professional',
            colors: parsed.brand?.colors ?? [],
            fonts: parsed.brand?.fonts ?? [],
            layoutStyle: parsed.brand?.layoutStyle,
            mediaStyle: parsed.brand?.mediaStyle
        },
        businessContext: {
            siteName: parsed.businessContext?.siteName ?? '',
            siteDescription: parsed.businessContext?.siteDescription ?? '',
            industry: parsed.businessContext?.industry,
            services: parsed.businessContext?.services,
            location: parsed.businessContext?.location,
            keywords: parsed.businessContext?.keywords
        }
    }
}

export const buildSiteContext = async (
    environmentContext: EnvironmentContext,
    createExtArgs: CreateExtArgs
): Promise<SiteContext> => {
    const {serverFacade} = environmentContext

    const siteMetadata = getSiteMetadata(createExtArgs)
    const mainPageOutline = getMainPageOutline(createExtArgs)

    const requestData = {
        appDefId: promptHubAppId,
        promptId: proactiveAiBuildSiteContextPromptId,
        params: {
            site_name: siteMetadata.siteName ?? '',
            brand_colors: JSON.stringify(siteMetadata.brandColors ?? []),
            brand_fonts: JSON.stringify(siteMetadata.brandFonts ?? []),
            main_page_content: JSON.stringify(mainPageOutline)
        }
    }

    try {
        const response = await serverFacade.post(aiGatewayUrl, requestData)
        if (!response.response.generatedTexts || response.response.generatedTexts.length === 0) {
            throw new ReportableError({
                errorType: proactiveAiBuildContextErrorType,
                message: 'AI response missing generated texts',
                extras: {}
            })
        }
        const generatedText = response.response.generatedTexts[0]
        return parseAISiteContextResponse(generatedText)
    } catch (e: any) {
        throw new ReportableError({
            errorType: proactiveAiBuildContextErrorType,
            message: proactiveAiBuildContextErrorMessage,
            extras: {
                originalError: e?.message
            }
        })
    }
}
